<template>
  <div class="row">
    <div class="page-title">
      <div class="row">
        <div class="col-sm-6 col-12">
          <h2 v-if="isNewAccount">Create New Account</h2>
          <h2 v-else-if="account">{{ account.companyName }}</h2>
          <p v-if="account && account.accountCode" class="text-muted mb-0">{{ account.accountCode }}</p>
        </div>
        <div v-if="editAccountInfo" class="col-sm-6 col-12 text-end">
          <button v-if="!isNewAccount && account" class="btn btn-danger me-2" @click="toggleEditMode">
            <i class="fa fa-ban"></i> Cancel
          </button>

          <button v-if="isNewAccount" class="btn btn-danger me-2">
            <a :href="`/service-provider/${serviceProviderId}/accounts`" class="text-white">
              <i class="fa fa-arrow-left"></i> Go Back
            </a>
          </button>
        </div>
        <div v-if="!isNewAccount && !editAccountInfo && account" class="col-sm-6 col-12 text-end">
          <button class="btn btn-outline-secondary me-2" @click="toggleEditMode">
            <i class="fa fa-edit"></i> Edit
          </button>
          <div class="btn-group">
            <button class="btn btn-primary dropdown-toggle" type="button" data-bs-toggle="dropdown">
              Actions
            </button>
            <ul class="dropdown-menu">
              <li v-if="account.status !== 'ACTIVE'"><a href="#" role="menuitem" class="dropdown-item" @click.prevent="changeStatus('ACTIVE')"><i class="fa fa-check-circle"></i> Activate</a></li>
              <li v-if="account.status !== 'INACTIVE'"><a href="#" role="menuitem" class="dropdown-item" @click.prevent="changeStatus('INACTIVE')"><i class="fa fa-pause-circle"></i> Inactive</a></li>
              <li v-if="account.status !== 'CLOSED'"><a href="#" role="menuitem" class="dropdown-item" @click.prevent="changeStatus('CLOSED')"><i class="fa fa-times-circle"></i> Close</a></li>
              <li><hr class="dropdown-divider"></li>
              <li><a href="#" role="menuitem" class="dropdown-item" @click.prevent="generateInvoice"><i class="fa fa-file-invoice"></i> Generate Invoice</a></li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- Summary Cards (only shown for existing accounts in read-only mode) -->
    <div v-if="!isNewAccount && !editAccountInfo && account" class="row">
      <div class="col-md-3 col-sm-6 mb-3">
        <div class="card">
          <div class="card-body">
            <div class="d-flex align-items-center justify-content-between">
              <div>
                <h6 class="text-muted mb-1">Current Balance</h6>
                <h4 class="mb-0">{{ formatCurrency(account.currentBalance) }}</h4>
              </div>
              <div class="icon-box bg-primary">
                <i class="fa fa-dollar-sign text-white"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-3 col-sm-6 mb-3">
        <div class="card">
          <div class="card-body">
            <div class="d-flex align-items-center justify-content-between">
              <div>
                <h6 class="text-muted mb-1">Credit Limit</h6>
                <h4 class="mb-0">{{ account.creditLimit > 0 ? formatCurrency(account.creditLimit) : 'Unlimited' }}</h4>
              </div>
              <div class="icon-box bg-success">
                <i class="fa fa-credit-card text-white"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-3 col-sm-6 mb-3">
        <div class="card">
          <div class="card-body">
            <div class="d-flex align-items-center justify-content-between">
              <div>
                <h6 class="text-muted mb-1">Status</h6>
                <h4 class="mb-0"><span :class="getStatusClass(account.status)">{{ account.status }}</span></h4>
              </div>
              <div class="icon-box bg-info">
                <i class="fa fa-info-circle text-white"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-3 col-sm-6 mb-3">
        <div class="card">
          <div class="card-body">
            <div class="d-flex align-items-center justify-content-between">
              <div>
                <h6 class="text-muted mb-1">Billing Cycle</h6>
                <h4 class="mb-0">{{ formatBillingCycle(account.billingCycle) }}</h4>
              </div>
              <div class="icon-box bg-warning">
                <i class="fa fa-calendar text-white"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- jQuery Wizard for Create/Edit Mode -->
    <div v-if="editAccountInfo || isNewAccount" class="col-12">
      <div class="card height-equal">
        <div class="card-header">
          <h3>{{ isNewAccount ? 'New' : 'Edit' }} Account Information</h3>
          <p class="mb-0 text-muted">Complete the steps to {{ isNewAccount ? 'create' : 'update' }} the account.</p>
        </div>

        <div id="account-wizard">
          <div class="card-body">
            <div class="horizontal-wizard-wrapper">
              <div class="row">
                <!-- Navigation tabs -->
                <div class="col-12 main-horizontal-header mb-3 border-1">
                  <div class="nav nav-pills horizontal-options" role="tablist" aria-orientation="horizontal">
                    <a class="nav-link" data-bs-toggle="pill" href="#account-step-company" role="tab" aria-selected="true">
                      <div class="horizontal-wizard">
                        <div class="stroke-icon-wizard"><i class="fa fa-building"></i></div>
                        <div class="horizontal-wizard-content">
                          <h5 class="f-w-600">Company Details</h5>
                        </div>
                      </div>
                    </a>

                    <a class="nav-link" data-bs-toggle="pill" href="#account-step-billing" role="tab" aria-selected="false">
                      <div class="horizontal-wizard">
                        <div class="stroke-icon-wizard"><i class="fa fa-credit-card"></i></div>
                        <div class="horizontal-wizard-content">
                          <h5 class="f-w-600">Billing Settings</h5>
                        </div>
                      </div>
                    </a>

                    <a class="nav-link" data-bs-toggle="pill" href="#account-step-contact" role="tab" aria-selected="false">
                      <div class="horizontal-wizard">
                        <div class="stroke-icon-wizard"><i class="fa fa-user"></i></div>
                        <div class="horizontal-wizard-content">
                          <h5 class="f-w-600">Contact Info</h5>
                        </div>
                      </div>
                    </a>
                  </div>
                </div>

                <!-- Tab content -->
                <div class="col-12 tab-content dark-field p-4">
                  <!-- Step 1: Company Details -->
                  <div class="tab-pane fade active show wizard-content" id="account-step-company" role="tabpanel">
                    <div class="mb-3">
                      <label class="form-label f-w-600" for="account-company-name-input">
                        Company Name <span class="text-danger">*</span>
                      </label>
                      <input id="account-company-name-input" v-model="formData.companyName"
                             class="form-control" type="text" placeholder="Enter company name"/>
                      <div id="account-company-name-error" class="invalid-feedback d-none">
                        Please enter the company name.
                      </div>
                    </div>
                    <div class="mb-3">
                      <label class="form-label f-w-600" for="account-status-input">Status</label>
                      <select id="account-status-input" v-model="formData.status" class="form-select">
                        <option value="ACTIVE">Active</option>
                        <option value="Inactive">Inactive</option>
                        <option value="CLOSED">Closed</option>
                      </select>
                    </div>
                    <div class="mb-3">
                      <label class="form-label f-w-600" for="account-notes-input">Notes</label>
                      <textarea id="account-notes-input" v-model="formData.notes"
                                class="form-control" rows="3"
                                placeholder="Internal notes about this account"></textarea>
                    </div>
                  </div>

                  <!-- Step 2: Billing Settings -->
                  <div class="tab-pane fade wizard-content" id="account-step-billing" role="tabpanel">
                    <div class="mb-3">
                      <label class="form-label f-w-600" for="account-billing-cycle-input">
                        Billing Cycle <span class="text-danger">*</span>
                      </label>
                      <select id="account-billing-cycle-input" v-model="formData.billingCycle" class="form-select">
                        <option value="PER_TRIP">Per Trip</option>
                        <option value="WEEKLY">Weekly</option>
                        <option value="BIWEEKLY">Bi-Weekly</option>
                        <option value="MONTHLY">Monthly</option>
                        <option value="QUARTERLY">Quarterly</option>
                      </select>
                    </div>
                    <div class="mb-3">
                      <label class="form-label f-w-600" for="account-payment-terms-input">Payment Terms (Days)</label>
                      <input id="account-payment-terms-input" v-model.number="formData.paymentTermsDays"
                             type="number" class="form-control" placeholder="e.g., 30"/>
                      <small class="text-muted">Number of days until payment is due</small>
                    </div>
                    <div class="mb-3">
                      <label class="form-label f-w-600" for="account-credit-limit-input">Credit Limit</label>
                      <input id="account-credit-limit-input" v-model.number="formData.creditLimit"
                             type="number" step="0.01" class="form-control" placeholder="0.00 for unlimited"/>
                      <small class="text-muted">Maximum outstanding balance (0 for unlimited)</small>
                    </div>
                    <div class="mb-3">
                      <label class="form-label f-w-600">Auto-Invoice</label>
                      <div class="form-check form-switch">
                        <input v-model="formData.autoInvoice" class="form-check-input" type="checkbox" id="account-auto-invoice">
                        <label class="form-check-label" for="account-auto-invoice">
                          Automatically generate invoices based on billing cycle
                        </label>
                      </div>
                    </div>
                    <div class="mb-3">
                      <label class="form-label f-w-600">Auto-Send Invoice</label>
                      <div class="form-check form-switch">
                        <input v-model="formData.autoSendInvoice" class="form-check-input" type="checkbox" id="account-auto-send-invoice">
                        <label class="form-check-label" for="account-auto-send-invoice">
                          Automatically send invoices to customer email
                        </label>
                      </div>
                    </div>
                  </div>

                  <!-- Step 3: Contact Info -->
                  <div class="tab-pane fade wizard-content" id="account-step-contact" role="tabpanel">
                    <div class="mb-3">
                      <label class="form-label f-w-600" for="account-billing-email-input">
                        Billing Email <span class="text-danger">*</span>
                      </label>
                      <input id="account-billing-email-input" v-model="formData.billingEmail"
                             type="email" class="form-control" placeholder="billing@company.com"/>
                      <div id="account-billing-email-error" class="invalid-feedback d-none">
                        Please enter a valid billing email.
                      </div>
                    </div>
                    <div class="mb-3">
                      <label class="form-label f-w-600" for="account-billing-address-input">Billing Address</label>
                      <textarea id="account-billing-address-input" v-model="formData.billingAddress"
                                class="form-control" rows="3" placeholder="Full billing address"></textarea>
                    </div>
                    <div class="row">
                      <div class="col-md-6 mb-3">
                        <label class="form-label f-w-600" for="account-contact-name-input">Contact Person Name</label>
                        <input id="account-contact-name-input" v-model="formData.contactPersonName"
                               type="text" class="form-control" placeholder="John Doe"/>
                      </div>
                      <div class="col-md-6 mb-3">
                        <label class="form-label f-w-600" for="account-contact-phone-input">Contact Phone</label>
                        <input id="account-contact-phone-input" v-model="formData.contactPersonPhone"
                               type="tel" class="form-control" placeholder="+1234567890"/>
                        <div id="account-contact-phone-error" class="invalid-feedback d-none">
                          Please enter a valid phone number.
                        </div>
                      </div>
                    </div>
                    <div class="mb-3">
                      <label class="form-label f-w-600" for="account-contact-email-input">Contact Email</label>
                      <input id="account-contact-email-input" v-model="formData.contactPersonEmail"
                             type="email" class="form-control" placeholder="contact@company.com"/>
                      <div id="account-contact-email-error" class="invalid-feedback d-none">
                        Please enter a valid contact email.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="card-footer">
            <div id="account-wizard-navigation"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Tabbed Interface (only shown for existing accounts in read-only mode) -->
    <div v-if="!isNewAccount && !editAccountInfo && account" class="col-12">
      <div class="card">
        <div class="card-header card-no-border pb-0">
          <ul class="nav nav-tabs" id="account-detail-tab" role="tablist">
            <li class="nav-item">
              <a class="nav-link active" id="overview-tab" data-bs-toggle="tab" href="#overview" role="tab">
                <i class="fa fa-info-circle"></i> Overview
              </a>
            </li>
            <li class="nav-item">
              <a class="nav-link" id="rides-tab" data-bs-toggle="tab" href="#rides" role="tab">
                <i class="fa fa-taxi"></i> Rides
              </a>
            </li>
            <li class="nav-item">
              <a class="nav-link" id="invoices-tab" data-bs-toggle="tab" href="#invoices" role="tab">
                <i class="fa fa-file-invoice"></i> Invoices
              </a>
            </li>
          </ul>
        </div>
        <div class="card-body">
          <div class="tab-content" id="account-detail-tabContent">
            <!-- Overview Tab -->
            <div class="tab-pane fade show active" id="overview" role="tabpanel">
              <div class="row">
                <div class="col-md-6">
                  <h5 class="mb-3 fw-bold">Company Information</h5>
                  <table class="table table-borderless">
                    <tr><th>Company Name:</th><td>{{ account.companyName }}</td></tr>
                    <tr><th>Account Code:</th><td>{{ account.accountCode }}</td></tr>
                    <tr><th>Status:</th><td><span :class="getStatusClass(account.status)">{{ account.status }}</span></td></tr>
                    <tr><th>Notes:</th><td>{{ account.notes || 'N/A' }}</td></tr>
                  </table>
                </div>
                <div class="col-md-6">
                  <h5 class="mb-3 fw-bold">Billing Information</h5>
                  <table class="table table-borderless">
                    <tr><th>Billing Cycle:</th><td>{{ formatBillingCycle(account.billingCycle) }}</td></tr>
                    <tr><th>Payment Terms:</th><td>{{ account.paymentTermsDays }} days</td></tr>
                    <tr><th>Credit Limit:</th><td>{{ account.creditLimit > 0 ? formatCurrency(account.creditLimit) : 'Unlimited' }}</td></tr>
                    <tr><th>Auto-Invoice:</th><td><span :class="account.autoInvoice ? 'badge bg-success' : 'badge bg-secondary'">{{ account.autoInvoice ? 'Enabled' : 'Disabled' }}</span></td></tr>
                  </table>
                </div>
              </div>
              <div class="row mt-3">
                <div class="col-md-6">
                  <h5 class="mb-3 fw-bold">Contact Information</h5>
                  <table class="table table-borderless">
                    <tr><th>Contact Person:</th><td>{{ account.contactPersonName || 'N/A' }}</td></tr>
                    <tr><th>Contact Phone:</th><td>{{ account.contactPersonPhone || 'N/A' }}</td></tr>
                    <tr><th>Contact Email:</th><td>{{ account.contactPersonEmail || 'N/A' }}</td></tr>
                  </table>
                </div>
                <div class="col-md-6">
                  <h5 class="mb-3 fw-bold">Billing Contact</h5>
                  <table class="table table-borderless">
                    <tr><th>Billing Email:</th><td>{{ account.billingEmail }}</td></tr>
                    <tr><th>Billing Address:</th><td>{{ account.billingAddress || 'N/A' }}</td></tr>
                  </table>
                </div>
              </div>
            </div>

            <!-- Rides Tab -->
            <div class="tab-pane fade" id="rides" role="tabpanel">
              <DataTable
                  v-if="account.id"
                  :ajax="{ url: ridesUrl }"
                  ref="ridesTable"
                  class="table-responsive display"
                  :columns="ridesColumns"/>
            </div>

            <!-- Invoices Tab -->
            <div class="tab-pane fade" id="invoices" role="tabpanel">
              <DataTable
                  v-if="account.id"
                  :ajax="{ url: invoicesUrl }"
                  ref="invoicesTable"
                  class="table-responsive display"
                  :columns="invoicesColumns"/>
            </div>

          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {ref, onMounted, onUnmounted, computed, nextTick, reactive} from 'vue';
import DataTable from 'datatables.net-vue3';
import DataTablesCore from 'datatables.net-bs5';
import {invoiceStatus, currency, zonedDateTime, rideStatus} from '../utility/DatatableRenderer';
import 'jquery-ui';
import './../../extras/js/jquery-wizard';
import {showToast} from '../utility/toastNotificationUtility';
import {Model} from '../modelTemplates/modelTemplates';
import Swal from "../../extras/js/sweetalert/sweetalert2.min";
import {newEntityId} from '../globals.js';

DataTable.use(DataTablesCore);

const props = defineProps({
  accountId: {type: String, required: true},
  serviceProviderId: {type: String, required: true}
});

const account = ref(null);
const editAccountInfo = ref(false);
const ridesTable = ref(null);
const invoicesTable = ref(null);
const isSaving = ref(false);

const isNewAccount = ref(props.accountId === newEntityId);
let wizardInstance = null;

// Initialize formData with Account model template
const AccountModel = Model('Account');
const accountModel = AccountModel();
const formData = reactive(accountModel.state);

const ridesUrl = computed(() => `/api/datatable/account-rides/${props.serviceProviderId}/${props.accountId}`);
const invoicesUrl = computed(() => `/api/datatable/account-invoices/${props.serviceProviderId}/${props.accountId}`);

onMounted(async () => {
  if (!isNewAccount.value) {
    loadAccount();
  } else {
    editAccountInfo.value = true;
  }

  await nextTick();
  if (editAccountInfo.value || isNewAccount.value) {
    setupWizard();
  }
});

onUnmounted(() => {
  if (wizardInstance) {
    try {
      $('#account-wizard').wizard('destroy');
    } catch (e) {
      console.warn('Failed to destroy wizard', e);
    }
  }
});

const toggleEditMode = async () => {
  editAccountInfo.value = !editAccountInfo.value;
  if (editAccountInfo.value && !isNewAccount.value) {
    // Load account data into form
    Object.assign(formData, account.value);
  }
  await nextTick();
  if (editAccountInfo.value) {
    setupWizard();
  } else if (wizardInstance) {
    try {
      $('#account-wizard').wizard('destroy');
      wizardInstance = null;
    } catch (e) {
      console.warn('Failed to destroy wizard', e);
    }
  }
};

const cancelEdit = () => {
  if (wizardInstance) {
    try {
      $('#account-wizard').wizard('destroy');
      wizardInstance = null;
    } catch (e) {
      console.warn('Failed to destroy wizard on cancel', e);
    }
  }

  if (isNewAccount.value) {
    window.location.href = `/service-provider/${props.serviceProviderId}/account`;
  } else {
    // Reset form data
    Object.assign(formData, account.value);
    toggleEditMode();
  }
};

async function saveAccount() {
  if (isSaving.value) return false;

  isSaving.value = true;
  try {
    const url = isNewAccount.value
      ? `/api/service-providers/${props.serviceProviderId}/account`
      : `/api/service-providers/${props.serviceProviderId}/account/${props.accountId}`;

    const method = isNewAccount.value ? 'POST' : 'PUT';

    const response = await fetch(url, {
      method: method,
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify(formData)
    });

    if (!response.ok) {
      const errorText = await response.text();
      showToast({
        title: 'Save failed',
        body: errorText || 'Could not save the account',
        type: 'error',
        delay: 8000
      });
      return false;
    }

    const savedAccount = await response.json();

    showToast({
      title: 'Success',
      body: `Account ${isNewAccount.value ? 'created' : 'updated'} successfully`,
      type: 'success'
    });

    if (isNewAccount.value) {
      // Redirect to the newly created account
      window.location.href = `/service-provider/${props.serviceProviderId}/account/${savedAccount.id}`;
    } else {
      // Reload account data and exit edit mode
      await loadAccount();
      if (wizardInstance) {
        try {
          $('#account-wizard').wizard('destroy');
          wizardInstance = null;
        } catch (e) {
          console.warn('Failed to destroy wizard on save', e);
        }
      }
      toggleEditMode();
    }

    return true;
  } catch (error) {
    console.error('Error saving account:', error);
    showToast({
      title: 'Network error',
      body: 'Could not save the account. Please try again.',
      type: 'error'
    });
    return false;
  } finally {
    isSaving.value = false;
  }
}

async function loadAccount() {
  try {
    const response = await fetch(`/api/service-providers/${props.serviceProviderId}/account/${props.accountId}`);
    if (response.ok) {
      account.value = await response.json();
    }
  } catch (error) {
    console.error('Error loading account:', error);
  }
}

async function changeStatus(newStatus) {
  const result = await Swal.fire({
    title: 'Change Account Status',
    text: `Are you sure you want to change the account status to ${newStatus}?`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Yes, change it',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#3085d6',
    cancelButtonColor: '#6c757d'
  });

  if (result.isConfirmed) {
    try {
      const response = await fetch(`/api/account/${props.accountId}/status`, {
        method: 'PUT',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({status: newStatus})
      });
      
      if (response.ok) {
        await Swal.fire({
          title: 'Success',
          text: `Account status changed to ${newStatus}`,
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
        loadAccount();
      } else {
        Swal.fire({
          title: 'Error',
          text: 'Failed to change account status',
          icon: 'error'
        });
      }
    } catch (error) {
      console.error('Error changing status:', error);
      Swal.fire({
        title: 'Error',
        text: 'Network error occurred while changing status',
        icon: 'error'
      });
    }
  }
}

async function generateInvoice() {
  const result = await Swal.fire({
    title: 'Generate Invoice',
    text: 'Generate a new invoice for this account?',
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Yes, generate',
    cancelButtonText: 'Cancel',
    confirmButtonColor: '#28a745',
    cancelButtonColor: '#6c757d'
  });

  if (result.isConfirmed) {
    try {
      const response = await fetch(`/api/invoice/generate/${props.accountId}`, {
        method: 'POST'
      });
      
      if (response.ok) {
        await Swal.fire({
          title: 'Success',
          text: 'Invoice generated successfully',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
        if (invoicesTable.value?.dt) invoicesTable.value.dt.ajax.reload();
      } else {
        const errorText = await response.text();
        Swal.fire({
          title: 'Error',
          text: errorText || 'Failed to generate invoice',
          icon: 'error'
        });
      }
    } catch (error) {
      console.error('Error generating invoice:', error);
      Swal.fire({
        title: 'Error',
        text: 'Network error occurred while generating invoice',
        icon: 'error'
      });
    }
  }
}

function formatCurrency(value) {
  return currency(value);
}

function formatBillingCycle(cycle) {
  const cycles = {
    'PER_TRIP': 'Per Trip',
    'WEEKLY': 'Weekly',
    'BIWEEKLY': 'Bi-Weekly',
    'MONTHLY': 'Monthly',
    'QUARTERLY': 'Quarterly'
  };
  return cycles[cycle] || cycle;
}

function getStatusClass(status) {
  const classes = {
    'ACTIVE': 'badge bg-success',
    'INACTIVE': 'badge bg-warning',
    'CLOSED': 'badge bg-danger'
  };
  return classes[status] || 'badge bg-secondary';
}

const ridesColumns = [
  {
    data: "origin",
    title: "Pickup",
    render: (data, type, row) => {
      return row?.originLocationData?.address|| 'N/A';
    }
  },
  {
    data: "destination",
    title: "Destination",
    render: (data, type, row) => {
      return row?.destinationLocationData?.address|| 'N/A';
    }
  },
  {data: "rideStatus", title: "Status", render: rideStatus},
  {
    data: "fare", title: "Fare",
    render: (data, type, row) => {
      // totalWithTax is what the rider paid; totalFare is the pre-tax commission base. Formatted,
      // because the raw column serialises at whatever scale it was stored with.
      if (!row?.rideFare) return 'N/A';
      const charged = row.rideFare.totalWithTax ?? row.rideFare.totalFare;
      return '$' + Number(charged || 0).toFixed(2);
    }
  },
  {data: "orderDateTime", title: "Date", render: zonedDateTime},
  {
    title: '', orderable: false, searchable: false, render: (data, type, row, meta) => {
      return `<a class="btn btn-sm btn-outline-primary" target="blank" href="/service-provider/${props.serviceProviderId}/ride/${row.id}">
                Details <i class="fa-solid fa-arrow-up-right-from-square"></i></a>`;
    }
  }
];

const invoicesColumns = [
  {title: "Invoice #", data: "invoiceNumber"},
  {title: "Period", data: "periodStart", render: (data, type, row) => `${data} - ${row.periodEnd}`},
  {title: "Amount", data: "totalAmount", render: currency},
  {title: "Status", data: "status", render: invoiceStatus},
  {title: "Due Date", data: "dueDate", render: zonedDateTime},
  {
    title: 'Actions',
    orderable: false,
    render: (data, type, row) => `
      <button class="btn btn-sm btn-outline-primary view-invoice" data-id="${row.id}">
        <i class="fa fa-eye"></i> View
      </button>
    `
  }
];


function companyStepIsValid() {
  return accountModel.validations.isCompanyNameValid.value && 
         accountModel.validations.isBillingCycleValid.value;
}

function billingStepIsValid() {
  return true; // All fields optional
}

function contactStepIsValid() {
  return accountModel.validations.isBillingEmailValid.value &&
         accountModel.validations.isContactEmailValid.value &&
         accountModel.validations.isContactPhoneValid.value;
}

function setupWizard() {
  if (wizardInstance) {
    try {
      $('#account-wizard').wizard('destroy');
    } catch (e) {
      console.warn('Failed to destroy existing wizard', e);
    }
  }

  wizardInstance = $('#account-wizard').wizard({
    step: '.nav-pills > a',
    classes: {
      step: {active: 'active'},
      pane: {active: 'active show'},
      button: {hide: 'd-none', disabled: 'd-none'},
    },
    buttonsAppendTo: '#account-wizard-navigation',
    templates: {
      buttons: function () {
        return `<div class="pull-left">
                  <a role="button" class="btn btn-outline-secondary mb-3" href="#account-wizard" data-wizard="back">Back</a>
                </div>
                <div class="pull-right">
                  <a role="button" class="btn btn-outline-primary mb-3" href="#account-wizard" data-wizard="next">Next</a>
                  <a role="button" class="btn btn-outline-success mb-3" href="#account-wizard" data-wizard="finish">Save</a>
                </div>`;
      }
    },
    validator: function (step) {
      let valid = true;
      if (step.index === 0) {
        valid = companyStepIsValid();
      } else if (step.index === 1) {
        valid = billingStepIsValid();
      } else if (step.index === 2) {
        valid = contactStepIsValid();
      }
      
      if (!valid) {
        showToast({
          title: 'Validation failed',
          body: 'Please complete the required fields for this step.',
          type: 'warn'
        });
      }
      return valid;
    },
    onFinish: function () {
      // Final validation before save
      if (!companyStepIsValid() || !billingStepIsValid() || !contactStepIsValid()) {
        showToast({
          title: 'Validation failed',
          body: 'Please complete all required fields.',
          type: 'warn'
        });
        return false;
      }
      
      saveAccount();
      return false; // Prevent default wizard navigation
    },
    onCancel: function () {
      cancelEdit();
      return false;
    }
  });
}
</script>

<style scoped>
.icon-box {
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
}
</style>
